import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

// Interface para el usuario actualizada con el rol de asistente
interface User {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  auth_method: string;
  picture?: string;
  // ✅ AGREGADO: Propiedades para identificar asistentes
  isAssistant?: boolean; 
  is_assistant?: boolean; // Por si viene con guion bajo de la base de datos
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Función para verificar el estado de login
  const checkLoginStatus = () => {
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');
    
    if (userData && loginStatus === 'true') {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Header - Error parsing user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('isLoggedIn');
        setUser(null);
        setIsLoggedIn(false);
      }
    } else {
      setUser(null);
      setIsLoggedIn(false);
    }
  };

  // Verificar estado inicial y escuchar cambios
  useEffect(() => {
    checkLoginStatus();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' || e.key === 'isLoggedIn') {
        checkLoginStatus();
      }
    };

    const handleAuthChange = () => {
      checkLoginStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogin', handleAuthChange);
    window.addEventListener('userLogout', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogin', handleAuthChange);
      window.removeEventListener('userLogout', handleAuthChange);
    };
  }, []);

  // También verificar cuando el componente recibe focus
  useEffect(() => {
    const handleFocus = () => {
      checkLoginStatus();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Cerrar menú de usuario al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Función para cerrar sesión
  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('token'); // ✅ Borrar token también
    
    setUser(null);
    setIsLoggedIn(false);
    setIsUserMenuOpen(false);
    
    window.dispatchEvent(new CustomEvent('userLogout'));
    navigate('/');
  };

  // ✅ LÓGICA CORREGIDA: Función para ir al dashboard correcto según el rol
  const goToDashboard = () => {
    if (user?.es_profesional) {
      navigate('/dashboard/profesional');
    } else if (user?.isAssistant || user?.is_assistant) {
      // Si es asistente, ir a su panel específico
      navigate('/assistant/dashboard');
    } else {
      // Si no es ninguno, es paciente
      navigate('/dashboard/paciente');
    }
    setIsUserMenuOpen(false);
  };

  // ✅ HELPER: Obtener etiqueta de rol correcta
  const getUserRoleLabel = () => {
    if (user?.es_profesional) return 'Profesional';
    if (user?.isAssistant || user?.is_assistant) return 'Secretaria/o';
    return 'Paciente';
  };

  const getInitials = (nombre: string, apellido: string) => {
    return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
  };

  // Componente del menú de usuario
  const UserMenu = () => (
    <div className="relative" ref={userMenuRef}>
      <button
        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
        className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt={`${user.nombre} ${user.apellido}`}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
            // ✅ Color diferente para secretarias (Púrpura) vs Médicos (Azul)
            user?.isAssistant || user?.is_assistant 
              ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
              : 'bg-gradient-to-r from-blue-500 to-green-500'
          }`}>
            {getInitials(user?.nombre || '', user?.apellido || '')}
          </div>
        )}
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-gray-900">
            {user?.nombre} {user?.apellido}
          </p>
          <p className="text-xs text-gray-500">
            {/* ✅ Usamos la función corregida para mostrar el rol */}
            {getUserRoleLabel()}
          </p>
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isUserMenuOpen && (
        <>
          <div 
            className="fixed inset-0 z-[998]" 
            onClick={() => setIsUserMenuOpen(false)}
          />
          
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-[999] animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="md:hidden px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">
                {user?.nombre} {user?.apellido}
              </p>
              <p className="text-xs text-gray-500">
                {user?.email}
              </p>
              <p className="text-xs text-blue-600">
                {/* ✅ Rol corregido en mobile también */}
                {getUserRoleLabel()}
              </p>
            </div>

            <button
              onClick={goToDashboard}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v0a2 2 0 002 2v0a2 2 0 002 2v0a2 2 0 012 2v0" />
              </svg>
              <span>Mi Menu</span>
            </button>

            {user?.es_profesional && (
              <button
                onClick={() => {
                  navigate('/professional/profile-config');
                  setIsUserMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Configuración</span>
              </button>
            )}

            {user?.es_profesional && (
              <button
                onClick={() => {
                  navigate('/professional/subscription');
                  setIsUserMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span>Mi suscripción</span>
              </button>
            )}

            <button
              onClick={() => {
                navigate('/ayuda');
                setIsUserMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Ayuda</span>
            </button>

            <div className="border-t border-gray-100 mt-2 pt-2">
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const GuestNavigation = () => (
    <>
      <nav className="hidden md:flex items-center space-x-6">
        {[
          { to: "/", label: "Inicio" },
          { to: "/search", label: "Buscar Profesionales" }
        ].map((item, idx) => (
          <Link
            key={idx}
            to={item.to}
            className="relative text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 group"
          >
            {item.label}
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
          </Link>
        ))}
        <Link
          to="/login"
          className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300"
        >
          Iniciar Sesión
        </Link>
        <Link
          to="/register"
          className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-2.5 rounded-full font-semibold shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300"
        >
          Registrarse
        </Link>
      </nav>

      {/* Mobile: botón Ingresar visible + hamburguesa */}
      <div className="md:hidden flex items-center space-x-2">
        <Link
          to="/login"
          className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-sm hover:shadow-md transition-all duration-300"
        >
          Ingresar
        </Link>
        <button
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
          </svg>
        </button>
      </div>
    </>
  );

  const AuthenticatedNavigation = () => (
    <>
      <nav className="hidden md:flex items-center space-x-8">
        {[
          { to: "/", label: "Inicio" },
          { to: "/search", label: "Buscar Profesionales" }
        ].map((item, idx) => (
          <Link
            key={idx}
            to={item.to}
            className="relative text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 group"
          >
            {item.label}
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
          </Link>
        ))}
        <UserMenu />
      </nav>

      <button
        className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Toggle menu"
      >
        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
        </svg>
      </button>
    </>
  );

  return (
    <header className="bg-white shadow-sm sticky top-0 z-[100] border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="bg-gradient-to-r from-blue-500 to-green-500 p-2 rounded-xl shadow-md group-hover:scale-105 transition-transform duration-300">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.5 3A2.5 2.5 0 0122 5.5c0 .63-.2 1.22-.55 1.7L19.2 9.7l2.55 2.55c.35.48.55 1.07.55 1.7a2.5 2.5 0 01-2.5 2.5c-.63 0-1.22-.2-1.7-.55L15.55 13.35l-2.55 2.55c-.48.35-1.07.55-1.7.55a2.5 2.5 0 01-2.5-2.5c0-.63.2-1.22.55-1.7L11.8 9.7 9.25 7.15C8.77 6.8 8.57 6.21 8.57 5.58A2.5 2.5 0 0111.07 3.08c.63 0 1.22.2 1.7.55L15.32 6.18l2.55-2.55C18.35 3.2 18.94 3 19.57 3H19.5z"/>
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-green-500 bg-clip-text text-transparent">
                consultAR
              </h1>
              {/* El eslogan anterior ("Tu salud, nuestra prioridad") le hablaba
                  al paciente, y el cliente es el profesional: el paciente no
                  elige ConsultAR, entra por el link que le manda su médico.
                  Este enumera lo que hace, sin prometer nada. */}
              <p className="text-xs text-gray-500 -mt-1">Turnos, pacientes y recetas</p>
            </div>
          </Link>

          {isLoggedIn ? <AuthenticatedNavigation /> : <GuestNavigation />}
        </div>

        {isMenuOpen && (
          <>
            <div 
              className="md:hidden fixed inset-0 bg-black/20 z-[90]"
              onClick={() => setIsMenuOpen(false)}
            />
            
          <div className="md:hidden pb-4 border-t border-gray-100 mt-4 bg-white rounded-lg shadow-lg fixed top-16 left-0 right-0 z-[95] mx-4">
            <div className="flex flex-col space-y-3 mt-4">
              {[
                { to: "/", label: "Inicio" },
                { to: "/search", label: "Buscar Profesionales" }
              ].map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                      navigate(item.to);
                      setIsMenuOpen(false);
                  }}
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 px-4 py-3 cursor-pointer hover:bg-blue-50 rounded-lg active:bg-blue-100"
                >
                  {item.label}
                </div>
              ))}

              {isLoggedIn ? (
                <>
                  <div className="border-t border-gray-100 pt-3 mt-3">
                    <button
                      type="button"
                      onClick={() => {
                        goToDashboard();
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                    >
                      {user?.picture ? (
                        <img
                          src={user.picture}
                          alt={`${user?.nombre} ${user?.apellido}`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                          // Color diferente en mobile también
                          user?.isAssistant || user?.is_assistant
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                            : 'bg-gradient-to-r from-blue-500 to-green-500'
                        }`}>
                          {getInitials(user?.nombre || '', user?.apellido || '')}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {user?.nombre} {user?.apellido}
                        </p>
                        <p className="text-xs text-blue-600">
                          {/* ✅ Rol corregido */}
                          {getUserRoleLabel()}
                        </p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        goToDashboard();
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 px-2 py-1 mt-2"
                    >
                      Mi Menu
                    </button>
                    
                    <button
                      onClick={() => {
                        navigate('/ayuda');
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 px-2 py-1"
                    >
                      Ayuda
                    </button>
                    
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left text-red-600 hover:text-red-700 font-medium transition-colors duration-200 px-2 py-1"
                    >
                      Cerrar Sesión
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div
                    onClick={() => {
                        navigate('/login');
                        setIsMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 px-2 py-1 cursor-pointer"
                  >
                    Iniciar Sesión
                  </div>
                  <div
                    onClick={() => {
                        navigate('/register');
                        setIsMenuOpen(false);
                    }}
                    className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-2.5 rounded-full font-semibold text-center shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                  >
                    Registrarse
                  </div>
                </>
              )}
            </div>
          </div>
          </>
        )}
      </div>

    </header>
  );
};

export default Header;